import { CSSProperties, memo, useMemo } from "react";

import { Typography } from "@mui/material";
import { palette, Styles } from "../../../styles/theme";
import { getAvatarColor } from "../../../utils/themeUtils";
import { Box } from "../Box";
import { Icon } from "../Icon";
import { MaterialSymbolType } from "../Icon/IconConstant";

const useStyles = (noBorder?: boolean): Styles => ({
  container: {
    backgroundColor: "#555555",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    border: !noBorder ? `2px solid ${palette.white}` : undefined,
  },
  size_sm: {
    minHeight: "18px",
    minWidth: "18px",
    maxHeight: "18px",
    maxWidth: "18px",
    padding: "4px",

    "&>div": {
      fontSize: "8px !important",
      fontWeight: "600",
      lineHeight: "normal !important",
    },
  },
  size_md: {
    minHeight: "32px",
    minWidth: "32px",
    maxHeight: "32px",
    maxWidth: "32px",
    padding: "2px",
    "&>div": {
      fontSize: "12px !important",
      lineHeight: "normal !important",
    },
  },
  size_lg: {
    minHeight: "40px",
    minWidth: "40px",
    maxHeight: "40px",
    maxWidth: "40px",
    padding: "2px",
    "&>div": {
      fontSize: "14px !important",
      lineHeight: "normal !important",
    },
  },
  size_xl: {
    minHeight: "60px",
    minWidth: "60px",
    maxHeight: "60px",
    maxWidth: "60px",
    padding: "2px",
    "&>div": {
      fontSize: "24px !important",
      lineHeight: "normal !important",
    },
  },
  initials: {
    textAlign: "center",
    color: "#f1f1f1",
    fontStyle: "normal",
    fontWeight: "500",
  },
});

interface BaseProps {
  color?: string;
  imageUrl?: string;
  size?: "sm" | "md" | "lg" | "xl";
  noBorder?: boolean;
  onClick?: () => void;
}
interface PropsWithInitials extends BaseProps {
  initials: string;
  userId?: string;
}
interface PropsWithIcon extends BaseProps {
  icon: MaterialSymbolType;
}

type Props = PropsWithInitials | PropsWithIcon;

const Avatar = ({
  color = `hsl(0, 40%, 60%)`,
  imageUrl,
  size = "md",
  noBorder,
  onClick,
  ...props
}: Props) => {
  const styles = useStyles(noBorder);
  const avatarColor = useMemo(
    () =>
      "userId" in props && props.userId ? getAvatarColor(props.userId) : color,
    [props, color]
  );

  if (imageUrl) {
    return (
      <img
        style={
          {
            ...styles.container,
            ...styles[`size_${size}`],
            background: "transparent",
            padding: 0,
            cursor: onClick ? "pointer" : undefined,
          } as CSSProperties
        }
        onClick={onClick}
        src={imageUrl}
        alt="Avatar"
      />
    );
  }

  return (
    <Box
      sx={
        {
          ...styles.container,
          ...styles[`size_${size}`],
          cursor: onClick ? "pointer" : undefined,
        } as CSSProperties
      }
      style={{
        backgroundColor: avatarColor,
      }}
      onClick={onClick}
    >
      {"initials" in props && (
        <Typography component={"div"} sx={styles.initials}>
          {props.initials || ""}
        </Typography>
      )}
      {"icon" in props && (
        <Icon size={24} color={palette.grey.nearBlack} icon={props.icon} />
      )}
    </Box>
  );
};

export default memo(Avatar);
